/**
 * TanStack Query hooks — orchestration des passes d'extraction MÉTIER (9.AA.1b).
 *
 * Routes consommées (`apps/api/src/routes/extraction-orchestrations.ts`) :
 *   GET  /v1/extraction-orchestrations/plan?submission_id=…   prévisualisation
 *   GET  /v1/extraction-orchestrations?submission_id=…        historique
 *   POST /v1/extraction-orchestrations                        lancement → 202
 *   GET  /v1/extraction-orchestrations/:id                    état + bilan par passe
 *
 * Ces routes sont **humain-only** : ni clé API, ni MCP. Un lancement engage jusqu'à
 * 6 appels LLM payants (décision PO 9.AA.1 §3).
 *
 * Pourquoi des types locaux et non `@kollectivia/shared`
 * -----------------------------------------------------
 * Les schémas partagés couvrent les **entrées** (query, body) et sont réutilisés
 * ici. Les formes de **sortie** de ces trois routes sont construites à la main dans
 * le handler (projection wire explicite, anti-leak `raw_response`) et ne sont pas
 * dérivées d'un schéma Zod. Les décrire ici, à côté des hooks qui les consomment,
 * évite d'inventer un schéma partagé qui ne validerait rien.
 */

import type { ExtractionStatus } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { api } from "../api";

import { extractionsKeys } from "./extractions";

// -----------------------------------------------------------------------------
// Types wire
// -----------------------------------------------------------------------------

/** `running` tant qu'une passe travaille ; les trois autres sont terminaux. */
export type OrchestrationStatus = "running" | "done" | "partial" | "failed";

/** Motif pour lequel une passe métier n'a pas été retenue — jamais de saut muet. */
export type PassSkipReason = "aucune_section_correspondante" | "plafond_de_passes_atteint";

export type RecognizedSection = { raw: string; normalized: string; pass: string };
export type PlainSection = { raw: string; normalized: string };

/**
 * Bilan de réconciliation d'une passe.
 *
 * `null` côté passe = réconciliation **jamais atteinte** (échec en amont :
 * téléchargement, fournisseur, parse). Ce n'est PAS « zéro changement » — un bilan
 * à zéro veut dire « vérifié, rien à faire ». L'UI doit distinguer les deux, sinon
 * elle affirme à l'utilisateur que rien n'a bougé alors que rien n'a été vérifié.
 */
export type ReconciliationReport = {
  created: number;
  updated: number;
  preserved_override: number;
  removed_or_invalidated: number;
  failed: number;
  removed_field_keys: string[];
  shared_stale_fields: { field_key: string; reason: string; foreign_candidates: number }[];
};

/** Le plan, tel que renvoyé par la prévisualisation ET par le détail. */
export type ExtractionPlanWire = {
  recognized_sections: RecognizedSection[];
  /** Sections du vocabulaire qu'AUCUNE passe ne couvre — reportées, pas ignorées. */
  recognized_sections_without_pass: PlainSection[];
  /** Sections jamais vues : remontées telles quelles pour investigation. */
  unknown_sections: PlainSection[];
  selected_passes: string[];
  skipped_passes: { pass: string; reason: PassSkipReason }[];
  max_business_passes: number;
};

export type ExtractionPlanResponse = ExtractionPlanWire & {
  submission_id: string;
  /**
   * Document sur lequel l'identification a lu les sections — donc celui que ce plan
   * décrit. `null` si l'identification n'a pas laissé de citation exploitable.
   *
   * L'UI s'en sert pour ne pas proposer un lancement sur un AUTRE fichier : les
   * passes retenues décriraient alors le mauvais document (remarque P2 Codex,
   * 9.AA.1b). Le serveur refuse déjà l'écart en 409.
   */
  identification_file_id: string | null;
  /** Non-null si une orchestration tourne : l'UI propose de la suivre, pas d'en lancer une. */
  active_orchestration: { id: string; started_at: string } | null;
};

export type OrchestrationPass = {
  extraction_id: string;
  pass_id: string | null;
  status: ExtractionStatus;
  error_code: string | null;
  started_at: string | null;
  completed_at: string | null;
  reconciliation: ReconciliationReport | null;
};

export type ExtractionOrchestration = ExtractionPlanWire & {
  id: string;
  submission_id: string;
  file_id: string;
  status: OrchestrationStatus;
  started_at: string;
  completed_at: string | null;
  passes: OrchestrationPass[];
  summary: { total: number; pending: number; running: number; done: number; failed: number };
};

/** Ligne d'historique — projection maigre, sans plan ni bilan. */
export type OrchestrationListItem = {
  id: string;
  submission_id: string;
  file_id: string;
  status: OrchestrationStatus;
  started_at: string;
  completed_at: string | null;
};

export type StartOrchestrationInput = {
  submission_id: string;
  file_id: string;
  model_id?: string;
};

// -----------------------------------------------------------------------------
// Clés
// -----------------------------------------------------------------------------

export const orchestrationKeys = {
  all: ["extraction-orchestrations"] as const,
  plan: (submissionId: string) =>
    ["extraction-orchestrations", "plan", submissionId] as const satisfies QueryKey,
  list: (submissionId: string) =>
    ["extraction-orchestrations", "list", submissionId] as const satisfies QueryKey,
  detail: (id: string) => ["extraction-orchestrations", "detail", id] as const satisfies QueryKey,
};

export function isOrchestrationTerminal(status: OrchestrationStatus | undefined): boolean {
  return status === "done" || status === "partial" || status === "failed";
}

// -----------------------------------------------------------------------------
// Lectures
// -----------------------------------------------------------------------------

/**
 * Prévisualisation du plan. Gratuite : aucune écriture, aucun appel fournisseur.
 *
 * `retry: false` volontaire. Les deux échecs attendus sont des refus métier
 * définitifs — `409 IDENTIFICATION_REQUIRED` (la passe d'identification n'a pas
 * tourné) et `403`/`404` de portée. Les retenter ne change rien et retarde
 * l'affichage du message qui dit à l'utilisateur quoi faire.
 */
export function useExtractionPlan(
  submissionId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  const { enabled = true } = opts;
  return useQuery({
    queryKey: orchestrationKeys.plan(submissionId ?? "__none__"),
    queryFn: () =>
      api.get<ExtractionPlanResponse>(
        `/v1/extraction-orchestrations/plan?submission_id=${encodeURIComponent(submissionId!)}`,
      ),
    enabled: !!submissionId && enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Historique des orchestrations d'une soumission, la plus récente d'abord.
 *
 * C'est ce hook qui rend la **reprise après rafraîchissement** possible : au
 * montage, le panneau lit la plus récente — y compris terminée — et réaffiche son
 * bilan. Sans lui, recharger la page effacerait le résultat que l'utilisateur
 * venait de lire.
 */
export function useExtractionOrchestrations(submissionId: string | undefined) {
  return useQuery({
    queryKey: orchestrationKeys.list(submissionId ?? "__none__"),
    queryFn: () =>
      api.get<{ items: OrchestrationListItem[]; total: number }>(
        `/v1/extraction-orchestrations?submission_id=${encodeURIComponent(submissionId!)}&limit=10`,
      ),
    enabled: !!submissionId,
    refetchOnWindowFocus: false,
  });
}

/**
 * État + bilan par passe, avec polling optionnel.
 *
 * Le polling s'arrête **de lui-même** dès que le statut est terminal — `done`,
 * `partial` ou `failed`. Oublier `partial` laisserait l'interface interroger le
 * serveur indéfiniment sur une orchestration close.
 */
export function useExtractionOrchestration(
  id: string | undefined,
  opts: { pollIntervalMs?: number } = {},
) {
  const { pollIntervalMs = 0 } = opts;
  return useQuery({
    queryKey: orchestrationKeys.detail(id ?? "__none__"),
    queryFn: () => api.get<ExtractionOrchestration>(`/v1/extraction-orchestrations/${id}`),
    enabled: !!id,
    refetchInterval: (query) => {
      if (!pollIntervalMs) return false;
      const data = query.state.data as ExtractionOrchestration | undefined;
      if (!data) return pollIntervalMs;
      return isOrchestrationTerminal(data.status) ? false : pollIntervalMs;
    },
    refetchOnWindowFocus: false,
  });
}

// -----------------------------------------------------------------------------
// Écriture
// -----------------------------------------------------------------------------

/**
 * Lance l'orchestration. L'API répond **202** : le travail continue en arrière-plan.
 *
 * Les invalidations comptent autant que l'appel. Après un lancement :
 *   - le plan change (une orchestration devient active, donc le bouton doit se
 *     transformer en « suivi ») ;
 *   - l'historique gagne une entrée ;
 *   - la liste des extractions de la soumission gagne N lignes `pending` ;
 *   - le détail de la soumission porte `last_extraction`.
 *
 * En oublier une laisse une partie de l'écran figée sur un état périmé.
 */
export function useStartExtractionOrchestration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartOrchestrationInput) =>
      api.post<ExtractionOrchestration>(
        "/v1/extraction-orchestrations",
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (_row, vars) => {
      void qc.invalidateQueries({ queryKey: orchestrationKeys.plan(vars.submission_id) });
      void qc.invalidateQueries({ queryKey: orchestrationKeys.list(vars.submission_id) });
      void qc.invalidateQueries({
        queryKey: extractionsKeys.byParent("submission", vars.submission_id),
      });
      void qc.invalidateQueries({ queryKey: ["submissions", "detail", vars.submission_id] });
    },
  });
}
